/**
 * Asset Library folders, filters, bulk move and preview copy. Merge under `assets.library` in each
 * locale (kept out of `app/i18n.ts` so that shared file stays a single import and spread).
 */
export const assetFoldersCopy = {
  en: {
    library: {
      previewUnavailable: 'Preview unavailable',
      listColumns: {
        file: 'File',
        type: 'Type',
        location: 'Location',
        status: 'Status',
        size: 'Size',
        usage: 'Usage',
        noUses: 'Not used',
      },
      typeGroup: 'Asset type',
      controls: 'Find and filter assets',
      type: { all: 'All', IMAGE: 'Images', VIDEO: 'Video', AUDIO: 'Audio', OTHER: 'Other' },
      unfiled: 'Unfiled',
      unfiledHint: 'Not in a folder',
      notPublished: 'Not used in published content',
      rootEmpty: 'No files in Root',
      rootEmptyDescription: 'Your files in folders stay inside those folders. Open a folder or upload an asset here.',
      emptyFolder: 'This folder is empty',
      emptyFolderDescription:
        'Select assets in the library and choose Move to… to file them in this folder.',
      filteredEmptyDescription: 'Try removing a filter or searching for something else.',
      filters: {
        group: 'Asset filters',
        folder: 'Folder',
        allFolders: 'All folders',
        otherFolder: 'Another folder',
        orientation: 'Orientation',
        anyOrientation: 'Any orientation',
        usage: 'Used in',
        anyUsage: 'Any usage',
        source: 'Source',
        anySource: 'Any source',
        added: 'Date added',
        anyTime: 'Any time',
        tag: 'Tag',
        anyTag: 'Any tag',
        sort: 'Sort',
        clear: 'Clear filters',
      },
      orientation: { LANDSCAPE: 'Landscape', PORTRAIT: 'Portrait', SQUARE: 'Square' },
      usage: {
        USED: 'Used in published content',
        UNUSED: 'Not used in published content',
      },
      source: { UPLOAD: 'Uploaded', IMPORT: 'Imported', GENERATED: 'Generated' },
      added: {
        '7d': 'Last 7 days',
        '30d': 'Last 30 days',
        '90d': 'Last 90 days',
        '365d': 'Last year',
      },
      sort: {
        updated_desc: 'Recently updated',
        updated_asc: 'Least recently updated',
        added_desc: 'Newest first',
        added_asc: 'Oldest first',
        name_asc: 'Name A to Z',
        name_desc: 'Name Z to A',
        size_desc: 'Largest first',
        size_asc: 'Smallest first',
      },
      folders: {
        heading: 'Folders',
        root: 'Root',
        backToRoot: 'Back to Root',
        new: 'New folder',
        actions: 'Actions for folder {{name}}',
        rename: 'Rename',
        delete: 'Delete folder',
        inProject: 'Project folder',
        showFewer: 'Show fewer folders',
        showAll_one: 'Show all {{count}} folder',
        showAll_other: 'Show all {{count}} folders',
        truncated_one: 'Showing the first {{count}} folder.',
        truncated_other: 'Showing the first {{count}} folders.',
        empty: 'No folders yet.',
        emptyCanCreate: 'No folders yet. Use New folder to organize your assets.',
        loadFailed: 'Folders could not be loaded.',
        viewing: 'Viewing',
        showAllAssets: 'Show all assets',
        createTitle: 'New folder',
        createDescriptionCompany:
          'Company-wide folders organize Company assets. Names must be unique.',
        createDescriptionProject:
          'Project folders organize this Project’s assets. Names must be unique.',
        renameTitle: 'Rename folder',
        renameDescription: 'Renaming does not move or change the assets inside.',
        nameLabel: 'Folder name',
        namePlaceholder: 'e.g. Brand assets',
        createConfirm: 'Create folder',
        renameConfirm: 'Rename',
        createdToast: 'Folder “{{name}}” created.',
        renamedToast: 'Folder renamed to “{{name}}”.',
        deleteTitle: 'Delete folder “{{name}}”?',
        deleteEmpty: 'The folder is empty. Deleting it does not affect any asset.',
        deleteWithAssets_one:
          'This folder holds {{count}} asset. Deleting the folder never deletes assets: it moves them to Unfiled first. Published content and permissions are not affected.',
        deleteWithAssets_other:
          'This folder holds {{count}} assets. Deleting the folder never deletes assets: it moves them to Unfiled first. Published content and permissions are not affected.',
        deleteConfirm: 'Delete folder',
        deleteConfirmWithAssets_one: 'Move {{count}} asset to Unfiled and delete folder',
        deleteConfirmWithAssets_other: 'Move {{count}} assets to Unfiled and delete folder',
        deleting: 'Deleting…',
        deletedToast: 'Folder “{{name}}” deleted.',
        deletedWithAssetsToast_one: 'Folder “{{name}}” deleted. {{count}} asset moved to Unfiled.',
        deletedWithAssetsToast_other:
          'Folder “{{name}}” deleted. {{count}} assets moved to Unfiled.',
      },
      move: {
        action: 'Move to…',
        dropDockHint: 'Drop here to choose a folder',
        dragHint: 'Drag selected assets onto a folder, or use Move to…',
        moving: 'Moving assets…',
        noChange: 'These assets are already in this folder.',
        title_one: 'Move {{count}} asset',
        title_other: 'Move {{count}} assets',
        description:
          'Moving only changes the folder. The asset, its versions, where it is used and who can see it stay the same.',
        destination: 'Destination folder',
        noFolders: 'There are no folders for these assets yet. They can be moved to Root.',
        noEligibleFolders: 'No accessible folder matches these assets. Create a folder in the same Company/Project or move to Root.',
        companyScope: 'Company assets can be moved into Company folders only. Project folders are not available for this selection.',
        projectScope: 'Project assets can be moved only into folders of their own Project. Company folders are not eligible.',
        mixedScope:
          'The selection mixes Company and Project assets (or different Projects), so it can only be moved to Unfiled. Select assets from one scope to choose a folder.',
        confirm: 'Move',
        movedToast_one: 'Moved {{count}} asset to {{destination}}.',
        movedToast_other: 'Moved {{count}} assets to {{destination}}.',
        movedBanner_one: 'Moved {{count}} asset to {{destination}}.',
        movedBanner_other: 'Moved {{count}} assets to {{destination}}.',
        undo: 'Undo',
        undoing: 'Undoing…',
        undoneToast: 'Move undone.',
        undoFailed: 'The move could not be undone completely.',
      },
      errors: {
        generic: 'Something went wrong. Try again.',
        nameTaken: 'A folder with this name already exists here.',
        notEmpty:
          'The folder now contains assets. Reopen the delete dialog to move them to Unfiled.',
        folderGone: 'This folder is no longer available.',
        scopeMismatch:
          'Assets can only be moved to a folder of the same scope (Company or Project).',
        assetGone: 'One of the selected assets is no longer available.',
        conflict: 'This changed elsewhere. Reload and try again.',
        invalidName: 'Enter a name of 1 to 80 characters.',
        denied: 'You do not have permission to do this.',
      },
    },
  },
  fa: {
    library: {
      previewUnavailable: 'پیش‌نمایش در دسترس نیست',
      listColumns: {
        file: 'فایل',
        type: 'نوع',
        location: 'محل نگهداری',
        status: 'وضعیت',
        size: 'حجم',
        usage: 'استفاده',
        noUses: 'بدون استفاده',
      },
      typeGroup: 'نوع دارایی',
      controls: 'یافتن و فیلتر دارایی‌ها',
      type: { all: 'همه', IMAGE: 'تصاویر', VIDEO: 'ویدیو', AUDIO: 'صدا', OTHER: 'سایر' },
      unfiled: 'بدون پوشه',
      unfiledHint: 'خارج از پوشه',
      notPublished: 'در محتوای منتشرشده استفاده نشده',
      rootEmpty: 'فایلی در ریشه نیست',
      rootEmptyDescription: 'فایل‌های پوشه‌ها تنها داخل همان پوشه نمایش داده می‌شوند. یک پوشه باز کنید یا فایلی در ریشه بارگذاری کنید.',
      emptyFolder: 'این پوشه خالی است',
      emptyFolderDescription:
        'دارایی‌ها را در کتابخانه انتخاب کنید و «انتقال به…» را بزنید تا در این پوشه قرار بگیرند.',
      filteredEmptyDescription: 'یک فیلتر را بردارید یا چیز دیگری جست‌وجو کنید.',
      filters: {
        group: 'فیلترهای دارایی',
        folder: 'پوشه',
        allFolders: 'همه پوشه‌ها',
        otherFolder: 'پوشهٔ دیگر',
        orientation: 'جهت تصویر',
        anyOrientation: 'هر جهتی',
        usage: 'محل استفاده',
        anyUsage: 'هر وضعیتی',
        source: 'منبع',
        anySource: 'هر منبعی',
        added: 'تاریخ افزودن',
        anyTime: 'هر زمانی',
        tag: 'برچسب',
        anyTag: 'هر برچسبی',
        sort: 'مرتب‌سازی',
        clear: 'پاک کردن فیلترها',
      },
      orientation: { LANDSCAPE: 'افقی', PORTRAIT: 'عمودی', SQUARE: 'مربع' },
      usage: {
        USED: 'استفاده‌شده در محتوای منتشرشده',
        UNUSED: 'استفاده‌نشده در محتوای منتشرشده',
      },
      source: { UPLOAD: 'بارگذاری‌شده', IMPORT: 'واردشده', GENERATED: 'تولیدشده' },
      added: {
        '7d': '۷ روز گذشته',
        '30d': '۳۰ روز گذشته',
        '90d': '۹۰ روز گذشته',
        '365d': 'یک سال گذشته',
      },
      sort: {
        updated_desc: 'تازه‌ترین به‌روزرسانی',
        updated_asc: 'قدیمی‌ترین به‌روزرسانی',
        added_desc: 'جدیدترین افزوده‌شده',
        added_asc: 'قدیمی‌ترین افزوده‌شده',
        name_asc: 'نام (الف تا ی)',
        name_desc: 'نام (ی تا الف)',
        size_desc: 'بیشترین حجم',
        size_asc: 'کمترین حجم',
      },
      folders: {
        heading: 'پوشه‌ها',
        root: 'ریشه',
        backToRoot: 'بازگشت به ریشه',
        new: 'پوشهٔ جدید',
        actions: 'عملیات پوشهٔ {{name}}',
        rename: 'تغییر نام',
        delete: 'حذف پوشه',
        inProject: 'پوشهٔ پروژه',
        showFewer: 'نمایش پوشه‌های کمتر',
        showAll_one: 'نمایش همهٔ {{count}} پوشه',
        showAll_other: 'نمایش همهٔ {{count}} پوشه',
        truncated_one: 'فقط {{count}} پوشهٔ نخست نمایش داده می‌شود.',
        truncated_other: 'فقط {{count}} پوشهٔ نخست نمایش داده می‌شود.',
        empty: 'هنوز پوشه‌ای وجود ندارد.',
        emptyCanCreate: 'هنوز پوشه‌ای وجود ندارد. با «پوشهٔ جدید» دارایی‌ها را سازمان‌دهی کنید.',
        loadFailed: 'بارگذاری پوشه‌ها ناموفق بود.',
        viewing: 'در حال نمایش',
        showAllAssets: 'نمایش همهٔ دارایی‌ها',
        createTitle: 'پوشهٔ جدید',
        createDescriptionCompany:
          'پوشه‌های سراسری شرکت، دارایی‌های شرکت را سازمان می‌دهند. نام پوشه‌ها نباید تکراری باشد.',
        createDescriptionProject:
          'پوشه‌های پروژه، دارایی‌های همین پروژه را سازمان می‌دهند. نام پوشه‌ها نباید تکراری باشد.',
        renameTitle: 'تغییر نام پوشه',
        renameDescription: 'تغییر نام، دارایی‌های داخل پوشه را جابه‌جا یا دگرگون نمی‌کند.',
        nameLabel: 'نام پوشه',
        namePlaceholder: 'مثلاً فایل‌های برند',
        createConfirm: 'ساخت پوشه',
        renameConfirm: 'تغییر نام',
        createdToast: 'پوشهٔ «{{name}}» ساخته شد.',
        renamedToast: 'نام پوشه به «{{name}}» تغییر کرد.',
        deleteTitle: 'پوشهٔ «{{name}}» حذف شود؟',
        deleteEmpty: 'این پوشه خالی است. حذف آن روی هیچ دارایی اثری ندارد.',
        deleteWithAssets_one:
          'در این پوشه {{count}} دارایی هست. حذف پوشه هرگز دارایی‌ها را پاک نمی‌کند؛ ابتدا آن‌ها را به «بدون پوشه» منتقل می‌کند. محتوای منتشرشده و دسترسی‌ها تغییری نمی‌کنند.',
        deleteWithAssets_other:
          'در این پوشه {{count}} دارایی هست. حذف پوشه هرگز دارایی‌ها را پاک نمی‌کند؛ ابتدا آن‌ها را به «بدون پوشه» منتقل می‌کند. محتوای منتشرشده و دسترسی‌ها تغییری نمی‌کنند.',
        deleteConfirm: 'حذف پوشه',
        deleteConfirmWithAssets_one: 'انتقال {{count}} دارایی به «بدون پوشه» و حذف پوشه',
        deleteConfirmWithAssets_other: 'انتقال {{count}} دارایی به «بدون پوشه» و حذف پوشه',
        deleting: 'در حال حذف…',
        deletedToast: 'پوشهٔ «{{name}}» حذف شد.',
        deletedWithAssetsToast_one:
          'پوشهٔ «{{name}}» حذف شد و {{count}} دارایی به «بدون پوشه» رفت.',
        deletedWithAssetsToast_other:
          'پوشهٔ «{{name}}» حذف شد و {{count}} دارایی به «بدون پوشه» رفت.',
      },
      move: {
        action: 'انتقال به…',
        dropDockHint: 'برای انتخاب پوشه اینجا رها کنید',
        dragHint: 'دارایی‌های انتخاب‌شده را روی پوشه بکشید یا از «انتقال به…» استفاده کنید.',
        moving: 'در حال انتقال دارایی‌ها…',
        noChange: 'این دارایی‌ها از قبل داخل همین پوشه هستند.',
        title_one: 'انتقال {{count}} دارایی',
        title_other: 'انتقال {{count}} دارایی',
        description:
          'انتقال فقط پوشه را عوض می‌کند. خود دارایی، نسخه‌ها، محل‌های استفاده و دسترسی‌ها همان می‌مانند.',
        destination: 'پوشهٔ مقصد',
        noFolders:
          'هنوز پوشه‌ای برای این دارایی‌ها وجود ندارد. می‌توانید آن‌ها را به ریشه ببرید.',
        noEligibleFolders: 'پوشهٔ مجاز و هم‌حوزه برای این دارایی‌ها وجود ندارد. پوشه‌ای در شرکت یا پروژهٔ مربوط بسازید یا به ریشه انتقال دهید.',
        companyScope: 'دارایی شرکتی فقط به پوشهٔ شرکتی منتقل می‌شود. پوشه‌های پروژه برای این انتخاب مجاز نیستند.',
        projectScope: 'دارایی پروژه فقط به پوشه‌های همان پروژه منتقل می‌شود. پوشه‌های شرکتی برای این فایل‌ها مجاز نیستند.',
        mixedScope:
          'انتخاب شما دارایی‌های شرکت و پروژه (یا پروژه‌های متفاوت) را با هم دارد و فقط می‌توان آن را به «بدون پوشه» برد. برای انتخاب پوشه، دارایی‌های یک حوزه را انتخاب کنید.',
        confirm: 'انتقال',
        movedToast_one: '{{count}} دارایی به «{{destination}}» منتقل شد.',
        movedToast_other: '{{count}} دارایی به «{{destination}}» منتقل شد.',
        movedBanner_one: '{{count}} دارایی به «{{destination}}» منتقل شد.',
        movedBanner_other: '{{count}} دارایی به «{{destination}}» منتقل شد.',
        undo: 'واگرد',
        undoing: 'در حال واگرد…',
        undoneToast: 'انتقال واگرد شد.',
        undoFailed: 'واگرد انتقال کامل انجام نشد.',
      },
      errors: {
        generic: 'مشکلی پیش آمد. دوباره تلاش کنید.',
        nameTaken: 'پوشه‌ای با این نام اینجا وجود دارد.',
        notEmpty:
          'اکنون در این پوشه دارایی هست. پنجرهٔ حذف را دوباره باز کنید تا آن‌ها به «بدون پوشه» بروند.',
        folderGone: 'این پوشه دیگر در دسترس نیست.',
        scopeMismatch: 'دارایی‌ها فقط به پوشه‌ای هم‌حوزه (شرکت یا پروژه) منتقل می‌شوند.',
        assetGone: 'یکی از دارایی‌های انتخاب‌شده دیگر در دسترس نیست.',
        conflict: 'این مورد جای دیگری تغییر کرده است. صفحه را تازه کنید و دوباره تلاش کنید.',
        invalidName: 'نامی بین ۱ تا ۸۰ نویسه وارد کنید.',
        denied: 'مجوز انجام این کار را ندارید.',
      },
    },
  },
} as const;
